import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Check, X, ArrowLeft, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Pricing() {
  const { t } = useTranslation();
  const { user, isAuthenticated, authToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [plans, setPlans] = useState(null);
  const [lifetimeBundleInfo, setLifetimeBundleInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansResponse, lifetimeResponse] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/lifetime-bundle-info`)
        ]);
        setPlans(plansResponse.data);
        setLifetimeBundleInfo(lifetimeResponse.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load pricing plans');
      }
    };
    fetchData();
  }, []);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.plan === planId) {
      return;
    }

    if (planId === 'free') {
      navigate('/settings');
      toast.info(t('pricing.downgradeFree'));
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(
        `${API}/subscriptions/create`,
        {
          plan: planId,
          origin_url: window.location.origin,
          upgrade_timing: 'immediate'
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('pricing.upgradeFailed'));
      setLoading(null);
    }
  };

  const handleLifetimePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login?intent=lifetime');
      return;
    }

    if (user?.plan === 'lifetime' || user?.has_lifetime_access) {
      toast.info('Du hast bereits Lifetime Zugang!');
      return;
    }

    setLoading('lifetime');
    try {
      console.log('🚀 Starting Lifetime Bundle checkout...');
      const response = await axios.post(
        `${API}/lifetime-bundle/checkout`,
        { 
          plan: 'lifetime',
          origin_url: window.location.origin,
          upgrade_timing: 'immediate'
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      console.log('✅ Checkout response:', response.data);

      if (response.data.checkout_url) {
        console.log('🔗 Redirecting to:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('❌ No checkout_url in response:', response.data);
        toast.error('Checkout-URL fehlt. Bitte versuchen Sie es später erneut.');
        setLoading(null);
      }
    } catch (error) {
      console.error('❌ Lifetime checkout error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Laden des Checkouts');
      setLoading(null);
    }
  };

  const getLifetimePrice = () => {
    if (!lifetimeBundleInfo) return '199';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return '199';
    if (sold < 150) return '249';
    return null;
  };

  const getLifetimeBadge = () => {
    if (!lifetimeBundleInfo) return '';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return `Nur noch ${50 - sold} verfügbar`;
    if (sold < 150) return `Nur noch ${150 - sold} verfügbar`;
    return 'Ausverkauft';
  };

  const isLifetimeSoldOut = () => {
    if (!lifetimeBundleInfo) return false;
    return (lifetimeBundleInfo.sold_count || 0) >= 150;
  };

  if (!plans) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const getPlanDisplay = (tier) => {
    if (tier === 'free') return plans.free;
    return billingInterval === 'monthly' ? plans[tier].monthly : plans[tier].yearly;
  };

  const isCurrentPlan = (planId) => {
    return user?.plan === planId;
  };

  const getButtonText = (planId) => {
    if (!isAuthenticated) return t('pricing.getStarted');
    if (planId === 'free') {
      return isCurrentPlan('free') ? t('pricing.currentPlan') : t('pricing.downgrade');
    }
    if (isCurrentPlan(planId)) return t('pricing.currentPlan');
    
    const currentTier = user?.plan?.includes('basic') ? 'basic' : user?.plan?.includes('pro') ? 'pro' : 'free';
    const targetTier = planId.includes('basic') ? 'basic' : 'pro';
    
    if (currentTier === 'free') return t('pricing.upgrade');
    if (currentTier === 'basic' && targetTier === 'pro') return t('pricing.upgrade');
    if (currentTier === 'pro' && targetTier === 'basic') return t('pricing.downgrade');
    
    return t('pricing.changePlan');
  };

  // Define what each plan includes - CLEAR and NO CONFUSION
  const planFeatures = {
    free: {
      capacity: { customers: 1, projects: 1 },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'PDF-Rechnungen erstellen'
      ],
      notIncluded: [
        t('pricing.features.automatic_invoices.title', 'Automatic invoices'),
        t('pricing.features.dashboard.title', 'Dashboard & Analytics'),
        t('pricing.taxCalculation', 'Tax calculation')
      ]
    },
    basic: {
      capacity: { customers: 2, projects: 4 },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'PDF-Rechnungen erstellen',
        'Automatische Rechnungen aus Zeiteinträgen',
        'Dashboard mit Umsatzübersicht',
        'Finanz-Auswertungen'
      ],
      notIncluded: [
        t('pricing.features.tax_overview.title', 'Tax calculation (DE/AT/CH)')
      ]
    },
    pro: {
      capacity: { customers: '∞', projects: '∞' },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'PDF-Rechnungen erstellen',
        'Automatische Rechnungen aus Zeiteinträgen',
        'Dashboard mit Umsatzübersicht',
        'Finanz-Auswertungen',
        t('pricing.features.tax_overview.title', 'Tax calculation (DE/AT/CH)'),
        'Einkommensteuer, USt, Sozialabgaben'
      ],
      notIncluded: []
    }
  };

  const renderPlanCard = (tier, isPopular = false) => {
    const plan = getPlanDisplay(tier);
    const planId = plan.name;
    const features = planFeatures[tier];
    
    return (
      <div
        key={planId}
        className={`relative bg-white rounded-2xl shadow-xl p-8 flex flex-col ${
          isPopular ? 'ring-2 ring-emerald-500 transform scale-105' : ''
        }`}
      >
        {isPopular && (
          <div className="absolute top-0 right-0 bg-emerald-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
            {t('pricing.popular')}
          </div>
        )}

        {/* Plan Header */}
        <div className="text-center mb-6">
          <h3 className="text-3xl font-bold text-gray-900 mb-2">
            {t(`pricing.plans.${tier}.name`)}
          </h3>
          <p className="text-gray-600 mb-4">
            {t(`pricing.plans.${tier}.description`)}
          </p>
          <div className="mb-4">
            <span className="text-5xl font-bold text-gray-900">
              €{plan.price}
            </span>
            {plan.interval && (
              <>
                <span className="text-gray-600 ml-2">
                  / {plan.interval === 'month' ? t('pricing.perMonth') : t('pricing.perYear')}
                </span>
                <p className="text-xs text-gray-500 mt-2">zzgl. MwSt.</p>
              </>
            )}
            {!plan.interval && (
              <span className="text-gray-600 ml-2 block text-sm mt-1">Kostenlos für immer</span>
            )}
          </div>
        </div>

        {/* Capacity */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
          <div className="text-sm text-gray-700">
            <span className="font-semibold">{features.capacity.customers}</span> {features.capacity.customers === 1 ? 'Kunde' : 'Kunden'} • {' '}
            <span className="font-semibold">{features.capacity.projects}</span> {features.capacity.projects === 1 ? 'Projekt' : 'Projekte'}
          </div>
        </div>

        {/* Included Features */}
        <div className="flex-1 mb-6">
          <p className="text-sm font-bold text-gray-800 mb-3">✓ Enthalten:</p>
          <ul className="space-y-2">
            {features.included.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Not Included */}
        {features.notIncluded.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-bold text-gray-600 mb-3">✗ Nicht enthalten:</p>
            <ul className="space-y-2">
              {features.notIncluded.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <X className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={() => handleUpgrade(planId)}
          disabled={isCurrentPlan(planId) || loading === planId}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
            isCurrentPlan(planId)
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : isPopular
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        >
          {loading === planId ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              {t('common.loading')}
            </span>
          ) : (
            getButtonText(planId)
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
      {/* Header with Back Button and Language */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          {/* Back Button - Left */}
          {isAuthenticated && (
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all text-gray-700 hover:text-gray-900 font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('common.back')}
            </Link>
          )}
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('pricing.subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={billingInterval === 'monthly' ? 'font-semibold' : 'text-gray-600'}>
              {t('pricing.monthly')}
            </span>
            <button
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 transition-colors hover:bg-gray-400"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingInterval === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={billingInterval === 'yearly' ? 'font-semibold' : 'text-gray-600'}>
              {t('pricing.yearly')}
              <span className="ml-2 text-emerald-600 font-semibold">
                ({t('pricing.save')} 17%)
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards - 4 columns with Lifetime Bundle */}
        <div className={`grid ${!isLifetimeSoldOut() && lifetimeBundleInfo && getLifetimePrice() ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6 max-w-7xl mx-auto mb-16`}>
          {renderPlanCard('free')}
          {renderPlanCard('basic', true)}
          {renderPlanCard('pro')}
          
          {/* Lifetime Bundle Card */}
          {!isLifetimeSoldOut() && lifetimeBundleInfo && getLifetimePrice() && (
            <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-2xl p-8 border-2 border-amber-400 flex flex-col">
              <div className="absolute -top-3 left-0 right-0 flex justify-center">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full font-bold text-xs flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Early Supporter
                  <Sparkles className="h-3 w-3" />
                </div>
              </div>

              {/* Plan Header */}
              <div className="text-center mb-4 mt-3">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Lifetime</h3>
                <p className="text-gray-600 mb-3 text-sm">Einmalig zahlen – für immer nutzen</p>
                <div className="mb-3">
                  <span className="text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    €{getLifetimePrice()}
                  </span>
                  <span className="text-gray-600 block text-xs mt-1">Einmalig – kein Abo</span>
                  <p className="text-xs text-gray-500 mt-1">zzgl. MwSt.</p>
                </div>
                <div className="inline-block bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
                  <span className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                    <span className="animate-pulse">🔥</span>
                    {getLifetimeBadge()}
                  </span>
                </div>
              </div>

              {/* Capacity */}
              <div className="mb-4 p-3 bg-white/70 rounded-lg">
                <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">∞</span> Kunden • <span className="font-semibold">∞</span> Projekte
                </div>
              </div>

              {/* Features */}
              <div className="flex-1 mb-4">
                <p className="text-sm font-bold text-gray-800 mb-2">✓ Enthalten:</p>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Alle Pro Features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Automatische Rechnungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Dashboard & Analytics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Steuerberechnung (DE/AT/CH)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Lebenslanger Zugang</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Alle zukünftigen Updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Nie wieder zahlen</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleLifetimePurchase}
                disabled={loading === 'lifetime'}
                className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white transition-all transform hover:scale-105 disabled:opacity-50"
              >
                {loading === 'lifetime' ? 'Lädt...' : '🎉 Jetzt sichern'}
              </button>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <p className="text-gray-600 mb-2">
            {t('pricing.allPlansInclude')}: {t('pricing.securePayments')}, {t('pricing.cancelAnytime')}
          </p>
          <p className="text-sm text-gray-500">
            Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
